import axios, { type AxiosRequestConfig } from "axios";

const baseURL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api";

export const apiClient = axios.create({
  baseURL,
  timeout: 15_000,
  headers: { "Content-Type": "application/json" },
});

// ─── Request interceptor: attach tenant slug + JWT access token ────────────
apiClient.interceptors.request.use((config) => {
  // FormData (file upload) cần browser tự set "multipart/form-data; boundary=..."
  // — default header "application/json" ở trên đè mất boundary nếu không xoá,
  // khiến BE (parser_classes = [MultiPartParser]) reject với "Unsupported media type".
  if (config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  }

  // Tenant slug từ Zustand tenant store (legacy) hoặc localStorage trực tiếp.
  const tenantSlug =
    (import.meta.env.VITE_DEFAULT_TENANT as string | undefined) ??
    window.localStorage.getItem("tenant_slug");
  if (tenantSlug) {
    config.headers["X-Tenant-Slug"] = tenantSlug;
  }

  // JWT access token từ Zustand auth store (persisted vào localStorage).
  // Lazy-read để tránh circular import với store.
  const authRaw = window.localStorage.getItem("sep2026-auth");
  if (authRaw) {
    try {
      const auth = JSON.parse(authRaw);
      const access = auth?.state?.accessToken;
      if (access) {
        config.headers.Authorization = `Bearer ${access}`;
      }
    } catch {
      // ignore parse error
    }
  }
  return config;
});

// ─── Response interceptor: auto-refresh 401 → retry once ──────────────────
type RetryConfig = AxiosRequestConfig & { _retry?: boolean };

apiClient.interceptors.response.use(
  (res) => res,
  async (err) => {
    const config = err.config as RetryConfig | undefined;

    // Tránh infinite loop: chỉ retry 1 lần + bypass cho /auth/refresh/ chính nó
    if (
      err.response?.status === 401 &&
      config &&
      !config._retry &&
      !config.url?.endsWith("/auth/refresh/") &&
      !config.url?.endsWith("/auth/login/")
    ) {
      config._retry = true;
      // Lazy import store tránh circular (store import client → client import store)
      const { useAuthStore } = await import("@/stores/auth");
      const newAccess = await useAuthStore.getState().refresh();
      if (newAccess) {
        config.headers = config.headers ?? {};
        (config.headers as Record<string, string>).Authorization = `Bearer ${newAccess}`;
        return apiClient(config);
      }
    }
    return Promise.reject(err);
  },
);
